import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import { FormDialog } from "@/components/shared/FormDialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { 
  Form, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { SelectValue, SelectTrigger, SelectContent, SelectItem, Select } from "@/components/ui/select";

// Define schema for tenant configuration
const tenantSchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  domain: z.string().optional(),
  subdomain: z.string().optional(),
  logo_url: z.string().optional(),
  subscription_plan: z.string().optional(),
  subscription_status: z.string().optional(),
  subscription_expiry: z.string().optional(),
  primary_color: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Valid hex color required").optional(),
  secondary_color: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Valid hex color required").optional(),
  accent_color: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Valid hex color required").optional(),
  language: z.string().optional(),
  timezone: z.string().optional(),
  currency: z.string().optional(),
  rtl: z.boolean().optional(),
  dark_mode: z.boolean().optional(),
});

const Settings = () => {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      company_name: "",
      domain: "",
      subdomain: "",
      logo_url: "",
      subscription_plan: "",
      subscription_status: "",
      subscription_expiry: "",
      primary_color: "#1E40AF",
      secondary_color: "#6B7280",
      accent_color: "#F59E0B",
      language: "en",
      timezone: "UTC",
      currency: "USD",
      rtl: false,
      dark_mode: false,
    }
  });

  const fetchTenantConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("tenant_config")
        .select("*")
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setTenantConfig(data);
        
        // Extract brand colors and settings from JSON objects
        const brandColors = data.brand_colors ? data.brand_colors : { primary: "#1E40AF", secondary: "#6B7280", accent: "#F59E0B" };
        const settings = data.settings ? data.settings : { language: "en", timezone: "UTC", currency: "USD", rtl: false, darkMode: false };
        
        form.reset({
          company_name: data.company_name,
          domain: data.domain || "",
          subdomain: data.subdomain || "",
          logo_url: data.logo_url || "",
          subscription_plan: data.subscription_plan || "",
          subscription_status: data.subscription_status || "",
          subscription_expiry: data.subscription_expiry || "",
          primary_color: brandColors.primary || "#1E40AF",
          secondary_color: brandColors.secondary || "#6B7280",
          accent_color: brandColors.accent || "#F59E0B",
          language: settings.language || "en",
          timezone: settings.timezone || "UTC",
          currency: settings.currency || "USD",
          rtl: settings.rtl || false,
          dark_mode: settings.darkMode || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error fetching configuration",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantConfig();
  }, []);

  const handleSaveSettings = async (values) => {
    try {
      // Prepare brand colors and settings objects
      const brandColors = {
        primary: values.primary_color || "#1E40AF",
        secondary: values.secondary_color || "#6B7280",
        accent: values.accent_color || "#F59E0B"
      };
      
      const settings = {
        language: values.language || "en",
        timezone: values.timezone || "UTC",
        currency: values.currency || "USD",
        rtl: values.rtl || false,
        darkMode: values.dark_mode || false
      };

      if (tenantConfig) {
        // Update
        const { error } = await supabase
          .from("tenant_config")
          .update({
            company_name: values.company_name,
            domain: values.domain || null,
            subdomain: values.subdomain || null,
            logo_url: values.logo_url || null,
            subscription_plan: values.subscription_plan || null,
            subscription_status: values.subscription_status || null,
            subscription_expiry: values.subscription_expiry || null,
            brand_colors: brandColors,
            settings: settings,
            updated_at: new Date().toISOString()
          })
          .eq("id", tenantConfig.id);

        if (error) throw error;
        toast({
          title: "Settings Updated",
          description: "Your settings have been updated successfully."
        });
      } else {
        // Create (first time setup)
        const { error } = await supabase
          .from("tenant_config")
          .insert({
            company_name: values.company_name,
            domain: values.domain || null,
            subdomain: values.subdomain || null,
            logo_url: values.logo_url || null,
            subscription_plan: values.subscription_plan || null,
            subscription_status: values.subscription_status || null,
            subscription_expiry: values.subscription_expiry || null,
            brand_colors: brandColors,
            settings: settings
          });

        if (error) throw error;
        toast({
          title: "Settings Created",
          description: "Your settings have been saved successfully."
        });
      }

      // Refresh data
      fetchTenantConfig();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <Card className="glass-card p-6 animate-fadeIn">
        <h2 className="text-2xl font-semibold mb-4">System Settings</h2>
        <p className="text-muted-foreground">
          Configure your application settings, branding, and subscription details.
        </p>
      </Card>

      {loading ? (
        <Card className="p-6">
          <div className="py-8 text-center">
            <div className="animate-pulse text-muted-foreground">Loading settings...</div>
          </div>
        </Card>
      ) : (
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
          </TabsList>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSaveSettings)} className="space-y-6 pt-6">
              <TabsContent value="general" className="space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Company Information</h3>
                  
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="company_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="domain"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Domain</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="example.com" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="subdomain"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subdomain</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="app" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="logo_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Logo URL</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://example.com/logo.png" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Regional Settings</h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="language"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Language</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="en">English</SelectItem>
                                <SelectItem value="fr">French</SelectItem>
                                <SelectItem value="es">Spanish</SelectItem>
                                <SelectItem value="de">German</SelectItem>
                                <SelectItem value="ja">Japanese</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="timezone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timezone</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select timezone" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="UTC">UTC</SelectItem>
                                <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                                <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                                <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                                <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                                <SelectItem value="Europe/London">London (GMT)</SelectItem>
                                <SelectItem value="Europe/Paris">Paris (CET)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Currency</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select currency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="USD">US Dollar ($)</SelectItem>
                              <SelectItem value="EUR">Euro (€)</SelectItem>
                              <SelectItem value="GBP">British Pound (£)</SelectItem>
                              <SelectItem value="JPY">Japanese Yen (¥)</SelectItem>
                              <SelectItem value="CAD">Canadian Dollar (C$)</SelectItem>
                              <SelectItem value="AUD">Australian Dollar (A$)</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="rtl"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                          <div className="space-y-0.5">
                            <FormLabel>Right-to-Left Support</FormLabel>
                            <FormDescription>
                              Enable for languages that read from right to left
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Card>
              </TabsContent>
              
              <TabsContent value="appearance" className="space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Color Scheme</h3>
                  
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="primary_color"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Color</FormLabel>
                          <div className="flex space-x-2">
                            <FormControl>
                              <Input {...field} type="text" />
                            </FormControl>
                            <Input 
                              type="color" 
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              className="w-12 p-1 h-10"
                            />
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="secondary_color"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Secondary Color</FormLabel>
                          <div className="flex space-x-2">
                            <FormControl>
                              <Input {...field} type="text" />
                            </FormControl>
                            <Input 
                              type="color" 
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              className="w-12 p-1 h-10"
                            />
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="accent_color"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Accent Color</FormLabel>
                          <div className="flex space-x-2">
                            <FormControl>
                              <Input {...field} type="text" />
                            </FormControl>
                            <Input 
                              type="color" 
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              className="w-12 p-1 h-10"
                            />
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="dark_mode"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                          <div className="space-y-0.5">
                            <FormLabel>Dark Mode</FormLabel>
                            <FormDescription>
                              Enable dark mode by default
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Card>
              </TabsContent>
              
              <TabsContent value="subscription" className="space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-medium mb-4">Subscription Details</h3>
                  
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="subscription_plan"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subscription Plan</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select plan" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="starter">Starter</SelectItem>
                              <SelectItem value="professional">Professional</SelectItem>
                              <SelectItem value="enterprise">Enterprise</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="subscription_status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subscription Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="suspended">Suspended</SelectItem>
                              <SelectItem value="canceled">Canceled</SelectItem>
                              <SelectItem value="trial">Trial</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="subscription_expiry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiry Date</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Card>
              </TabsContent>
              
              <div className="flex justify-end pt-4">
                <Button type="submit">
                  Save Settings
                </Button>
              </div>
            </form>
          </Form>
        </Tabs>
      )}
    </div>
  );
};

export default Settings;
